import { Link, useLocation } from 'react-router-dom'
import { type LucideIcon } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useSidebar } from '@/contexts/SidebarContext'
import { cn } from '@/lib/utils'

export interface SidebarNavItemProps {
  /** Display label */
  label: string
  /** Route path */
  href: string
  /** Lucide icon component */
  icon: LucideIcon
  /** Match these paths as active (defaults to exact href match) */
  matchPaths?: string[]
}

/**
 * Single navigation item in the sidebar.
 * Shows icon + text in expanded mode, icon-only with tooltip in collapsed mode.
 * Active state: left violet bar + violet background wash.
 */
export function SidebarNavItem({ label, href, icon: Icon, matchPaths }: SidebarNavItemProps) {
  const location = useLocation()
  const { collapsed, setMobileOpen } = useSidebar()

  const isActive = matchPaths
    ? matchPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))
    : location.pathname === href || location.pathname.startsWith(href + '/')

  // Special case: dashboard should only match exact "/"
  const isDashboard = href === '/'
  const active = isDashboard ? location.pathname === '/' : isActive

  const linkContent = (
    <Link
      to={href}
      onClick={() => setMobileOpen(false)}
      className={cn(
        'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all duration-150 relative',
        active
          ? 'bg-sidebar-accent/15 text-sidebar-accent-foreground'
          : 'text-sidebar-foreground hover:bg-sidebar-muted hover:text-sidebar-accent-foreground'
      )}
    >
      {/* Active indicator bar */}
      {active && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-sidebar-accent" />
      )}
      <Icon className={cn('h-4 w-4 shrink-0', active ? 'text-sidebar-accent' : 'text-sidebar-foreground group-hover:text-sidebar-accent-foreground')} />
      {!collapsed && <span className="truncate">{label}</span>}
    </Link>
  )

  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {label}
        </TooltipContent>
      </Tooltip>
    )
  }

  return linkContent
}
